import { useInfiniteQuery } from 'react-query';
import { Organization } from 'views/App/Portal/LexID/Organizations/_types/organization';
import { ApiCollectionResponse } from 'types/api_response';
import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';

export default function useOrganizations<T extends Organization>(
  params: Record<string, any> = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexid/admin-org/v1/organizations`;
  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['organizations', params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        // pageParam is either undefined (first page) or the offset number
        const paramString = createParamString({
          ...params,
          ...(pageParam !== undefined && { offset: pageParam }),
        });
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: `${url}?${paramString}`,
          options: { signal },
        });
        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        // Return just the offset number, not the full URL
        // The query function will construct the full URL with current params
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          return lastPage.offset;
        }
      },
      ...options,
    }
  );
}
